/*
 * Runtime configuration, read once at startup.
 *
 * CLAUDE.md: "API base URL comes from env; no PII in build artifacts." Both halves matter here.
 * The first is why nothing below has a production default — a missing variable must stop the app
 * loudly at boot, not resolve to localhost in a deployed bundle and produce a confusing CORS
 * error. The second is why this file reads only these four values: everything Vite sees on
 * `import.meta.env` with a VITE_ prefix is inlined into the bundle and served to the public, and
 * a bundle is not a place to find out what got inlined.
 */

interface Config {
  readonly apiUrl: string;
  readonly oidcIssuer: string;
  readonly oidcClientId: string;
  readonly oidcRedirectUri: string;
  /**
   * Extra query parameters the primary provider requires on the authorization request.
   *
   * ── Why the primary needs what only the alt used to have — 08 Sep 2026 ────────────────────────
   *
   * `AltProvider.authorizeParams` below carries the argument: WorkOS refuses a bare authorization
   * request with `invalid-connection-selector`, and `provider=authkit` is the selector. That was an
   * alt-only concern for exactly as long as WorkOS was only ever the second door — which stopped
   * being true when staging dropped the fake IdP and WorkOS became its only provider.
   *
   * A build with no second provider and an unusable first one is the worst arrangement available,
   * and it would have failed as a redirect to error.workos.com with nothing in our logs. So the
   * field is here for the same reason it is there, and it stays optional: a provider that needs no
   * selector is configured with none.
   */
  readonly oidcAuthorizeParams?: Readonly<Record<string, string>>;
  readonly altOidc?: AltProvider;
  /**
   * A same-origin prefix to route the token exchange through, for local diagnosis only.
   *
   * ── Why this exists — 19 Aug 2026 ─────────────────────────────────────────────────────────────
   *
   * WorkOS attaches `access-control-allow-origin` to a 2xx and **omits it on every error**. So the
   * one response an operator needs to read — the one explaining why a sign-in failed — is the one
   * the browser forbids the page from reading, and the network tab cannot show it either. A failed
   * exchange is therefore indistinguishable from a misconfigured allow list, which cost most of a
   * day.
   *
   * Set this and the exchange goes to the dev server instead, which forwards it and returns the
   * answer same-origin, where nothing is hidden. **Unset everywhere else**, and deliberately so:
   * a proxy in the authentication path is a substitution, and local dev §1 is clear that one which
   * is always on stops being a diagnostic and starts being the thing under test.
   *
   * A prefix, not a route: the provider key is appended to it (`/idp/primary`, `/idp/alt`) and
   * `vite.config.ts` mounts one forwarder per configured issuer. That is not decoration — the
   * single shared mount it replaced sent every exchange to whichever issuer was listed first,
   * which broke local sign-in outright for the whole of 19–20 Aug 2026.
   */
  readonly oidcErrorProxy?: string;
  /**
   * Whether to offer the seeded-identity door on the sign-in screen — M-21.5.
   *
   * The local IdP holds every seeded principal and will sign you in as any of them from a list.
   * That is how the browser suite works and how anybody exercises a role they do not have a
   * password for, and until now the only way to reach it was a button that also claimed to be a
   * normal way in. It is now what it actually is: a development door, labelled as one.
   *
   * **Default off, and off is the safe direction.** A variable nobody set leaves production
   * without the door, which is a build somebody notices; the reverse would put a 'sign in as
   * anyone' list on a live product, which nobody notices until it matters. Only the
   * environments that render it true get it.
   */
  readonly seededIdentities: boolean;
  /**
   * The shortest password the form will submit — M-37.1a.
   *
   * A hint, not a rule. The API re-checks it and the provider applies a strength floor on top that
   * no number here can express, so a value that has drifted from the dashboard makes this screen
   * *less helpful*, never more permissive. That asymmetry is why it is allowed to be a public
   * build-time constant at all.
   *
   * It is here rather than hardcoded because it used to be hardcoded in two files kept in step by
   * a comment, and they went out of step with the provider the first time the dashboard changed.
   */
  readonly minimumPasswordLength: number;
}

/**
 * A second identity provider, when one is configured.
 *
 * ── Why an app can have two — 19 Aug 2026 ───────────────────────────────────────────────────────
 *
 * On a laptop the fake IdP authenticates every seeded principal — the browser suite and both seed
 * scripts depend on it — while WorkOS authenticates a real person against a manually provisioned
 * agency. Both are the same realm, both are live, and the API trusts both key sets (see
 * `RealmIdentities` in `identity`). The browser cannot infer which one a visitor wants, so it asks:
 * one button per configured provider.
 *
 * The same shape is what a provider migration needs in a deployed environment, which is why this
 * is a general capability rather than a development flag. Absent means one button and no change.
 *
 * **`issuer` here is a discovery base, not an `iss`.** For WorkOS the two differ: the browser
 * fetches `<application>/.well-known/openid-configuration` while every token is stamped with the
 * *environment's* issuer. Nothing in a browser verifies a token — the API does that — so this
 * value is only ever "where the endpoints are described".
 */
export interface AltProvider {
  readonly issuer: string;
  readonly clientId: string;
  /**
   * Extra query parameters the provider requires on the authorization request.
   *
   * ── Why this is configuration and not a branch in the client ─────────────────────────────────
   *
   * WorkOS refuses a bare authorization request with `invalid-connection-selector`: the standard
   * parameters do not say *which* flow is meant — hosted login, a named SSO connection, a specific
   * organisation — and it will not guess. `provider=authkit` selects the hosted login, and it is a
   * WorkOS word that appears in no other provider's vocabulary.
   *
   * The OIDC client is deliberately provider-agnostic — it learns its endpoints from a discovery
   * document precisely so that swapping providers is configuration. Writing `provider=authkit`
   * into it would undo that for the sake of one string, and the next provider would need its own
   * branch beside it. So the client appends whatever pairs it is given and never knows what they
   * mean; the runbook explains what WorkOS needs.
   *
   * Written as a query fragment — `a=b&c=d` — because that is the shape of the thing it becomes.
   */
  readonly authorizeParams?: Readonly<Record<string, string>>;
}

/**
 * `a=b&c=d` from the environment into pairs, or nothing.
 *
 * `URLSearchParams` rather than a hand-rolled split: it decodes percent-escapes, which a value
 * carrying a URL or a space will need, and it is the same parser that will re-encode them.
 */
function authorizeParams(raw: string | undefined): Readonly<Record<string, string>> | undefined {
  if (!raw) {
    return undefined;
  }
  const parsed = Object.fromEntries(new URLSearchParams(raw));
  return Object.keys(parsed).length > 0 ? parsed : undefined;
}

function optionalProvider(
  issuer: string | undefined,
  clientId: string | undefined,
  params: string | undefined,
): AltProvider | undefined {
  // Both or neither. Half a provider is a button that fails after the redirect, which is the worst
  // place for a configuration mistake to surface.
  if (!issuer || !clientId) {
    return undefined;
  }
  return { issuer, clientId, authorizeParams: authorizeParams(params) };
}

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is not set. The recruiter app has no default for it — a build that silently ` +
        'pointed at the wrong API would be worse than one that refuses to start.',
    );
  }
  return value;
}

export const config: Config = {
  apiUrl: required('VITE_API_URL', import.meta.env.VITE_API_URL),
  oidcIssuer: required('VITE_OIDC_ISSUER', import.meta.env.VITE_OIDC_ISSUER),
  oidcClientId: required('VITE_OIDC_CLIENT_ID', import.meta.env.VITE_OIDC_CLIENT_ID),
  // Derived rather than configured: it must match a value the provider has registered, and the
  // origin is known at runtime. One fewer variable to get wrong per environment.
  oidcRedirectUri: `${window.location.origin}/auth/callback`,
  oidcAuthorizeParams: authorizeParams(
    import.meta.env.VITE_OIDC_AUTHORIZE_PARAMS as string | undefined,
  ),
  altOidc: optionalProvider(
    import.meta.env.VITE_OIDC_ALT_ISSUER as string | undefined,
    import.meta.env.VITE_OIDC_ALT_CLIENT_ID as string | undefined,
    import.meta.env.VITE_OIDC_ALT_AUTHORIZE_PARAMS as string | undefined,
  ),
  // Absent in every environment that is not somebody's laptop mid-investigation. See the field.
  oidcErrorProxy: (import.meta.env.VITE_OIDC_ERROR_PROXY as string | undefined) || undefined,
  // Explicit string compare: 'false' is a truthy string, and every other boolean in this
  // codebase that got this wrong turned a guard off by setting it to the word false.
  seededIdentities: import.meta.env.VITE_SEEDED_IDENTITIES === 'true',
  // Falls back rather than trusting the parse: an unset or malformed variable must produce the
  // default, not `NaN`, which every comparison below would silently answer false for.
  minimumPasswordLength:
    Number(import.meta.env.VITE_AUTH_MIN_PASSWORD_LENGTH) > 0
      ? Number(import.meta.env.VITE_AUTH_MIN_PASSWORD_LENGTH)
      : 8,
};
